import { Component, OnDestroy, OnInit, inject } from '@angular/core'

import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'

import { Observable, Subject, of } from 'rxjs'

import {
  catchError,
  finalize,
  map,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  shareReplay
} from 'rxjs/operators'

import { NgSelectModule } from '@ng-select/ng-select'

// import { Store } from '@ngrx/store'

import { Business } from '../../../store/business/business.state'
// import { selectAllBusinesses } from '../../../store/business/business.selectors'
// import { loadBusinesses } from '../../../store/business/business.actions'

import { PaginationComponent } from '../template/pagination/pagination.component'

import { Payment, EarningsService, PaymentResponse } from '../../../services/earnings.service'

import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { BusinessService } from '../../../services/business.service'

@Component({
  selector: 'app-earnings',

  standalone: true,

  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgSelectModule,
    PaginationComponent,
    CapitalizePipe
  ],

  templateUrl: './earnings.component.html',
  styleUrl: './earnings.component.scss'
})
export class EarningsComponent implements OnInit, OnDestroy {
  //   private store = inject(Store)
  private earningsService = inject(EarningsService)

  private destroyed$ = new Subject<void>()
  private businessService = inject(BusinessService)

  // =========================================================
  // BUSINESS
  // =========================================================

  //   businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  businesses$!: Observable<Business[]>

  businessId: string | null = null

  // =========================================================
  // EARNINGS
  // =========================================================

  //   earnings$!: Observable<Payment[]>

  earnings: Payment[] = []

  // =========================================================
  // PAGINATION
  // =========================================================

  currentPage = 1

  pageLimit = 10

  totalCount = 0

  pageLastRecord = 0

  // =========================================================
  // FILTERS
  // =========================================================

  search = ''
  private searchSubject = new Subject<string>()
  private searchTimeout: ReturnType<typeof setTimeout> | null = null

  status: string | null = null

  // =========================================================
  // COUNTS
  // =========================================================

  successfulCount = 0

  // Keep this also because old HTML/code may use successCount
  successCount = 0

  pendingCount = 0

  failedCount = 0

  totalAmount = 0

  // =========================================================
  // LOADING
  // =========================================================

  loading = false

  // =========================================================
  // CONSTRUCTOR
  // =========================================================

  constructor() {}

  // =========================================================
  // INIT
  // =========================================================

  ngOnInit(): void {
    this.loadBusinesses()

    this.loadEarnings()
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(search => {
        this.search = search
        this.currentPage = 1
        this.loadEarnings()
      })
  }

  // =========================================================
  // LOAD BUSINESSES
  // =========================================================

  //   private loadBusinesses(): void {
  //     this.store.dispatch(
  //       loadBusinesses({
  //         page: 1,
  //         limit: 1000
  //       } as any)
  //     )
  //   }

  //   private loadBusinesses(): void {
  //     this.businesses$ = this.businessService.getAllBusinesses(false, true).pipe(
  //       map(response => response.businesses),
  //       catchError(error => {
  //         console.error('Error loading businesses:', error)

  //         return of([])
  //       }),
  //       takeUntil(this.destroyed$)
  //     )
  //   }

  private loadBusinesses(): void {
    this.businesses$ = this.businessService.getAllBusinesses(false, true).pipe(
      map(response => response.businesses ?? []),

      catchError(error => {
        console.error('Error loading businesses:', error)
        return of([])
      }),

      takeUntil(this.destroyed$),

      shareReplay(1)
    )
  }

  // =========================================================
  // LOAD EARNINGS
  // =========================================================

  //   loadEarnings(): void {
  //     this.loading = true

  //     const request = {
  //       page: this.currentPage,
  //       limit: this.pageLimit,
  //       businessId: this.businessId,
  //       status: this.status,
  //       search: this.search?.trim() || ''
  //     }

  //     this.earnings$ = this.earningsService.getPayments(request).pipe(
  //       map((response: PaymentResponse) => {
  //         const payments: Payment[] = response?.data ?? response?.payments ?? response?.results ?? []

  //         // Total records
  //         this.totalCount =
  //           response?.totalCount ?? response?.total ?? response?.count ?? payments.length

  //         // Successful count
  //         this.successfulCount =
  //           response?.successfulCount ??
  //           response?.successCount ??
  //           payments.filter(payment => this.isSuccessful(payment.status)).length

  //         this.successCount = this.successfulCount

  //         // Pending count
  //         this.pendingCount =
  //           response?.pendingCount ??
  //           payments.filter(payment => this.isPending(payment.status)).length

  //         // Failed count
  //         this.failedCount =
  //           response?.failedCount ?? payments.filter(payment => this.isFailed(payment.status)).length

  //         // Total amount
  //         // this.totalAmount = payments.reduce((total, payment) => {
  //         //   const amount = Number(payment.amount ?? 0)

  //         //   return total + (isNaN(amount) ? 0 : amount)
  //         // }, 0)
  //         this.totalAmount = Number(Number(response?.['totalAmount'] ?? 0).toFixed(2))

  //         // Last record shown in pagination
  //         this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)

  //         return payments
  //       }),

  //       catchError(error => {
  //         console.error('Error loading earnings:', error)

  //         this.earnings = []

  //         this.totalCount = 0
  //         this.pageLastRecord = 0

  //         this.successfulCount = 0
  //         this.successCount = 0
  //         this.pendingCount = 0
  //         this.failedCount = 0
  //         this.totalAmount = 0

  //         return of([] as Payment[])
  //       }),

  //       finalize(() => {
  //         this.loading = false
  //       }),

  //       takeUntil(this.destroyed$)
  //     )

  //     this.earnings$.pipe(takeUntil(this.destroyed$)).subscribe(payments => {
  //       this.earnings = payments
  //     })
  //   }

  loadEarnings(): void {
    this.loading = true

    const request = {
      page: this.currentPage,
      limit: this.pageLimit,
      businessId: this.businessId || null,
      status: this.status || null,
      search: this.search.trim()
    }

    this.earningsService
      .getPayments(request)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.loading = false
        }),
        catchError(error => {
          console.error('Error loading earnings:', error)

          this.earnings = []
          this.totalCount = 0
          this.pageLastRecord = 0
          this.successfulCount = 0
          this.successCount = 0
          this.pendingCount = 0
          this.failedCount = 0
          this.totalAmount = 0

          return of(null)
        })
      )
      .subscribe((response: PaymentResponse | null) => {
        if (!response) {
          return
        }

        const payments: Payment[] = response?.data ?? response?.payments ?? response?.results ?? []

        this.earnings = payments

        this.totalCount =
          response?.totalCount ?? response?.total ?? response?.count ?? payments.length

        this.successfulCount =
          response?.successfulCount ??
          response?.successCount ??
          payments.filter(payment => this.isSuccessful(payment.status)).length

        this.successCount = this.successfulCount

        this.pendingCount =
          response?.pendingCount ??
          payments.filter(payment => this.isPending(payment.status)).length

        this.failedCount =
          response?.failedCount ?? payments.filter(payment => this.isFailed(payment.status)).length

        this.totalAmount = Number(Number(response?.['totalAmount'] ?? 0).toFixed(2))

        this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
      })
  }

  // =========================================================
  // RESPONSE HANDLER
  // =========================================================

  private handlePaymentResponse(response: Payment[] | PaymentResponse): void {
    let payments: Payment[] = []

    // ---------------------------------------------------------
    // API directly returns array
    // ---------------------------------------------------------

    if (Array.isArray(response)) {
      payments = response

      this.totalCount = response.length
    }

    // ---------------------------------------------------------
    // API returns object
    // ---------------------------------------------------------
    else if (response) {
      payments = response.data ?? response.payments ?? response.results ?? []

      this.totalCount = response.totalCount ?? response.total ?? response.count ?? payments.length

      this.successfulCount = response.successfulCount ?? response.successCount ?? 0

      this.successCount = this.successfulCount

      this.pendingCount = response.pendingCount ?? 0

      this.failedCount = response.failedCount ?? 0
    }

    this.earnings = payments

    // ---------------------------------------------------------
    // If backend doesn't send counts, calculate from current
    // response
    // ---------------------------------------------------------

    if (this.successfulCount === 0 && this.pendingCount === 0 && this.failedCount === 0) {
      this.calculateCounts(payments)
    }

    // this.calculateTotalAmount(payments)

    this.calculatePageLastRecord()
  }

  // =========================================================
  // CALCULATE COUNTS
  // =========================================================

  private calculateCounts(payments: Payment[]): void {
    this.successfulCount = payments.filter(payment => this.isSuccessful(payment.status)).length

    this.successCount = this.successfulCount

    this.pendingCount = payments.filter(payment => this.isPending(payment.status)).length

    this.failedCount = payments.filter(payment => this.isFailed(payment.status)).length
  }

  // =========================================================
  // STATUS HELPERS
  // =========================================================

  private isSuccessful(status?: string): boolean {
    const value = String(status ?? '').toLowerCase()

    return ['success', 'successful', 'succeeded', 'paid', 'completed', 'complete'].includes(value)
  }

  private isPending(status?: string): boolean {
    const value = String(status ?? '').toLowerCase()

    return ['pending', 'processing', 'initiated', 'created'].includes(value)
  }

  private isFailed(status?: string): boolean {
    const value = String(status ?? '').toLowerCase()

    return ['failed', 'failure', 'cancelled', 'canceled', 'rejected'].includes(value)
  }

  // =========================================================
  // TOTAL AMOUNT
  // =========================================================

  private calculateTotalAmount(payments: Payment[]): void {
    this.totalAmount = payments.reduce((total, payment) => {
      const amount = Number(payment.amount ?? 0)

      return total + (isNaN(amount) ? 0 : amount)
    }, 0)
  }

  // =========================================================
  // PAGE LAST RECORD
  // =========================================================

  private calculatePageLastRecord(): void {
    if (this.totalCount === 0) {
      this.pageLastRecord = 0
      return
    }

    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  // =========================================================
  // PAGE CHANGE
  // =========================================================

  onPageChange(page: number): void {
    if (!page || page < 1) {
      return
    }

    this.currentPage = page

    this.loadEarnings()
  }

  // =========================================================
  // PAGE LIMIT CHANGE
  // =========================================================

  onPageLimitChange(): void {
    this.currentPage = 1

    this.loadEarnings()
  }

  // =========================================================
  // SET CURRENT PAGE
  // =========================================================

  setCurrentPage(): void {
    this.currentPage = 1
  }

  // =========================================================
  // BUSINESS FILTER
  // =========================================================

  onBusinessChange(): void {
    this.currentPage = 1

    this.loadEarnings()
  }

  // =========================================================
  // STATUS FILTER
  // =========================================================

  onStatusChange(): void {
    this.currentPage = 1

    this.loadEarnings()
  }

  // =========================================================
  // SEARCH
  // =========================================================

  //   onSearch(): void {
  //     this.currentPage = 1

  //     this.loadEarnings()
  //   }
  onSearch(value: string): void {
    this.search = value

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => {
      this.currentPage = 1
      this.loadEarnings()
    }, 500)
  }

  // =========================================================
  // CUSTOM BUSINESS SEARCH
  // =========================================================

  customSearchFn(term: string, item: Business): boolean {
    const searchTerm = String(term ?? '')
      .toLowerCase()
      .trim()

    if (!searchTerm) {
      return true
    }

    const businessName = String(item?.businessName ?? '').toLowerCase()

    const id = String(item?.id ?? '').toLowerCase()

    return businessName.includes(searchTerm) || id.includes(searchTerm)
  }

  // =========================================================
  // PAYMENT GATEWAY ID
  // =========================================================

  getPaymentGatewayId(payment: Payment): string {
    return payment.paymentGatewayId ?? payment.gatewayPaymentId ?? payment.paymentId ?? '-'
  }

  // =========================================================
  // BUSINESS NAME
  // =========================================================

  getBusinessName(payment: Payment): string {
    return payment.business?.businessName ?? '-'
  }

  // =========================================================
  // PAYMENT METHOD
  // =========================================================

  getPaymentDetails(earning: Payment): any {
    return earning?.['paymentResponse'] || null
  }

  // =========================================================
  // AMOUNT
  // =========================================================

  getAmount(payment: Payment): number {
    return Number(payment.amount ?? 0)
  }

  // =========================================================
  // STATUS CLASS
  // =========================================================

  getStatusClass(status?: string): string {
    const value = String(status ?? '').toLowerCase()

    if (this.isSuccessful(value)) {
      return 'badge-success'
    }

    if (this.isPending(value)) {
      return 'badge-warning'
    }

    if (this.isFailed(value)) {
      return 'badge-danger'
    }

    return 'badge-secondary'
  }

  // =========================================================
  // DISPLAY STATUS
  // =========================================================

  getStatusLabel(status?: string): string {
    if (!status) {
      return 'N/A'
    }

    return status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
  }

  // =========================================================
  // TRACK BY
  // =========================================================

  trackByPaymentId(index: number, payment: Payment): string | number {
    return payment.id ?? index
  }

  // =========================================================
  // DESTROY
  // =========================================================

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  getPaymentPurpose(earning: any): string {
    const description = earning.name ?? earning?.paymentResponse?.description

    if (!description) {
      return 'N/A'
    }

    // Adding 1 Licences
    const licenceMatch = description.match(/Adding\s+(\d+)\s+Licences?/i)

    if (licenceMatch) {
      const count = licenceMatch[1]

      // Agar API mein total licences available hain
      const totalLicences = earning?.paymentResponse?.notes?.newTotalLicences

      if (totalLicences) {
        // return `Add Licences (Total: ${totalLicences})`
        return `Add Licences`
      }

      return `Add Licences (${count})`
    }

    // Activation for plan: QA test 2
    if (/^Activation for plan:/i.test(description)) {
      return description
    }

    // Subscription Upgrade: 10 x Plan 1
    if (/^Subscription Upgrade:/i.test(description)) {
      return description
    }

    // Fallback
    return description
  }
}
